/**
 * RelationshipPanel — the object page's Relationships section (Page Templates
 * §3/§5; Design System §2.3). Renders an object's `entity_links` + owner/
 * dependency edges as a compact, navigable graph slice: People · Orgs · Tags ·
 * Files (the labels are Lexicon-relabelable per context). Because every object
 * answers "what's related" by walking the same edges (Object Model §7), one
 * panel serves every kind.
 *
 * Pure presentation: the server resolves edges to display labels + hrefs and
 * groups them; the block renders rows. It never traverses the graph itself.
 */

import * as React from "react";
import { RouterLink as NextLink } from "../router";
import { Card, CardContent, CardHeader, CardTitle } from "../card";
import { cn } from "../utils";

export interface RelationshipItem {
  key: string;
  label: string;
  href?: string;
}

export interface RelationshipGroup {
  /** Row label — "People", "Org", "Tags", "Files", … (Lexicon-relabelable). */
  label: string;
  items: RelationshipItem[];
}

export interface RelationshipPanelProps {
  groups: RelationshipGroup[];
  title?: React.ReactNode;
  emptyMessage?: React.ReactNode;
  /** Default true; pass false to render bare inside a parent Card. */
  wrapInCard?: boolean;
  className?: string;
}

function RelItem({ item }: { item: RelationshipItem }) {
  if (item.href) {
    return (
      <NextLink href={item.href} className="text-foreground hover:underline">
        {item.label}
      </NextLink>
    );
  }
  return <span className="text-foreground">{item.label}</span>;
}

export function RelationshipPanel({
  groups,
  title = "Relationships",
  emptyMessage = "Nothing linked yet.",
  wrapInCard = true,
  className,
}: RelationshipPanelProps) {
  const nonEmpty = groups.filter((g) => g.items.length > 0);
  const body =
    nonEmpty.length === 0 ? (
      <div className="px-6 py-2 type-body text-muted-foreground">
        {emptyMessage}
      </div>
    ) : (
      <div className="flex flex-col">
        {nonEmpty.map((group) => (
          <div
            key={group.label}
            className="flex gap-3 border-b border-border/40 px-6 py-2 first:pt-0 last:border-b-0 last:pb-0"
          >
            <span className="w-20 shrink-0 type-subtext text-muted-foreground">
              {group.label}
            </span>
            <span className="flex min-w-0 flex-1 flex-wrap gap-x-3 gap-y-1 type-body">
              {group.items.map((item) => (
                <RelItem key={item.key} item={item} />
              ))}
            </span>
          </div>
        ))}
      </div>
    );

  if (!wrapInCard) return <div className={className}>{body}</div>;

  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle>{title}</CardTitle>
      </CardHeader>
      <CardContent className="px-0">{body}</CardContent>
    </Card>
  );
}
